import { FC, JSX, useContext, useEffect, useState } from "react"
import { QESButton } from "@app/shared/qes-standards";
import { FlyoutContext, FlyoutFooter } from "@app/shared/Flyouts";
import { EcdColorsContext } from "../contexts/EcdColorsContext";
import { ColorSet } from "../interfaces";

interface EcdCustomizeColorsFlyoutProps {
  onChange: (change: boolean) => void;
}

export const EcdCustomizeColorsFlyout: FC<EcdCustomizeColorsFlyoutProps> = (props: EcdCustomizeColorsFlyoutProps) => {
  const flyoutContext = useContext(FlyoutContext);
  const colorsContext = useContext(EcdColorsContext);
  const [tempColors, setTempColors] = useState<ColorSet>({
    arrowNegative: colorsContext.arrowNegative,
    arrowPositive: colorsContext.arrowPositive,
    barHoverBackground: colorsContext.barHoverBackground,
    exceptionsPrimary: colorsContext.exceptionsPrimary,
    fillableGapsPrimary: colorsContext.fillableGapsPrimary,
    fillableGapsSecondary: colorsContext.fillableGapsSecondary,
    gapsPrimary: colorsContext.gapsPrimary,
    unfillableGaps: colorsContext.unfillableGaps,
  });
  const [tempExceptionsArray, setTempExceptionsArray] = useState(colorsContext.exceptionsArray);
  const [tempGapsArray, setTempGapsArray] = useState(colorsContext.gapsArray);

  useEffect(() => {
    setTempExceptionsArray(colorsContext.getNewExceptionsArray(tempColors.exceptionsPrimary));
  }, [tempColors.exceptionsPrimary]);

  useEffect(() => {
    setTempGapsArray(colorsContext.getNewGapsArray(tempColors.gapsPrimary));
  }, [tempColors.gapsPrimary]);

  useEffect(() => {
    props.onChange(haveColorsChanged());
  }, [tempColors]);

  const updateTempColor = (colorKey: keyof ColorSet, colorValue: string) => {
    const newColorSet = { ...tempColors };
    newColorSet[colorKey] = colorValue;
    setTempColors(() => newColorSet);
  }

  const handleSubmit = () => {
    colorsContext.updateColors(tempColors);
    // bypasses the "lose unsaved changes" warning
    flyoutContext.setIsFlyoutVisible(false);
  }

  const haveColorsChanged = () => {
    return Object.keys(tempColors).some((key: keyof ColorSet) => {
      return tempColors[key] !== colorsContext[key];
    });
  }

  const generateColorRow = (key: keyof ColorSet, displayName: string, colorBlockOverride?: JSX.Element) => {
    return (
      <li className="row ecd-colors__row align-items-start">
        <label className="col-4 m-0" htmlFor={key}>{displayName}</label>
        <div className="col-4">
          {colorBlockOverride || (
            <input
              id={key}
              className="mb-2"
              type="color"
              onChange={(e) => updateTempColor(key, e.target.value)}
              value={tempColors[key]}
              data-testid={`ecd-colors__color-picker--${key}`}
            />
          )}
        </div>
        <div className="col-4">
          <QESButton
            className="d-inline-flex align-items-center py-0"
            qesType="secondary"
            qesTestId={`ecd-colors-default--${key}`}
            onClick={() => updateTempColor(key, colorsContext.defaultColors[key])}
          >
            Restore Default:
            <div
              className="ecd-colors__block"
              style={{
                backgroundColor: colorsContext.defaultColors[key]
              }}
            />
          </QESButton>
        </div>
      </li>
    );
  }

  return (
    <>
      <ul className="ecd-colors__list">
        {generateColorRow("arrowNegative", "Negative Arrow")}
        {generateColorRow("arrowPositive", "Positive Arrow")}
        {generateColorRow("barHoverBackground", "Bar Hover Background")}
        {generateColorRow("exceptionsPrimary", "Exceptions/Justifications", (
          <>
            <input
              className="mb-2 mr-1"
              id="exceptionsPrimary"
              type="color"
              onChange={(e) => updateTempColor("exceptionsPrimary", e.target.value)}
              value={tempColors.exceptionsPrimary}
              data-testid="ecd-colors__color-picker--exceptions"
            />
            <div
              className="ecd-colors__block"
              style={{
                backgroundColor: tempExceptionsArray[1],
              }}
            />
            <div
              className="ecd-colors__block"
              style={{
                backgroundColor: tempExceptionsArray[2],
              }}
            />
            <div
              className="ecd-colors__block"
              style={{
                backgroundColor: tempExceptionsArray[3],
              }}
            />
            <div
              className="ecd-colors__block"
              style={{
                backgroundColor: tempExceptionsArray[4],
              }}
            />
          </>
        ))}
        {generateColorRow("fillableGapsPrimary", "Fillable")}
        {generateColorRow("fillableGapsSecondary", "Fillable by Single Provider")}
        {generateColorRow("gapsPrimary", "Gaps Gradient", (
          <>
            <input
              className="mb-2 mr-1"
              id="gapsPrimary"
              type="color"
              onChange={(e) => updateTempColor("gapsPrimary", e.target.value)}
              value={tempColors.gapsPrimary}
              data-testid="ecd-colors__color-picker--gaps"
            />
            <div
              className="ecd-colors__block"
              style={{
                backgroundColor: tempGapsArray[1],
              }}
            />
            <div
              className="ecd-colors__block"
              style={{
                backgroundColor: tempGapsArray[2],
              }}
            />
            <div
              className="ecd-colors__block"
              style={{
                backgroundColor: tempGapsArray[3],
              }}
            />
          </>
        ))}
        {generateColorRow("unfillableGaps", "Unfillable Gaps")}
      </ul>
      <QESButton
        className="pl-0"
        qesType="secondary"
        qesTestId="ecd-colors__restore-default--all"
        onClick={() => { setTempColors(colorsContext.defaultColors) }}
      >
        Restore All Defaults
      </QESButton>
      <FlyoutFooter
        submitButton={
          <QESButton
            qesType="primary"
            qesTestId="ecd-customize-colors__submit"
            onClick={handleSubmit}
          >
            Save Colors
          </QESButton>
        }
      />
    </>
  );
}
